import {DealPayrollGrade, DealPayrollGradeCode, isFinitePayrollNumber} from 'app/models/deal-payroll.model';
import {
  DECIMAL_ROUNDING_TOLERANCE, FOUR_DECIMAL_SCALE_FACTOR,
  MAXIMUM_COEFFICIENT_VALUE, MAXIMUM_HOURLY_RATE_VALUE
} from 'app/models/deal-payroll-settings.constants';
import {payrollNumberDisplay} from 'app/models/payroll-number-display';
import {DealPayrollGroupSettingsViewState} from './deal-payroll-group-settings.types';
import {previewGradeMonthlySalary} from './deal-payroll-group-settings.selectors';

const DEFAULT_REFERENCE_GRADE = 'JUNIOR_MINUS' as DealPayrollGradeCode;
const ZERO = 0;
const FULL_WEIGHT = 1;
const EMPTY_INPUT = '';

export class DealPayrollReferenceSalary {
  gradeCode = DEFAULT_REFERENCE_GRADE;
  private editing = false;
  private editingAmount: number = null;

  constructor(private view: () => DealPayrollGroupSettingsViewState, private changed: () => void) {}

  get grades(): DealPayrollGrade[] {
    const settings = this.view().settings;
    return (settings && settings.grades || []).filter(validReferenceGrade);
  }

  get grade(): DealPayrollGrade {
    return this.grades.find(grade => grade.gradeCode === this.gradeCode) || null;
  }

  get amount(): number {
    const settings = this.view().settings;
    return settings && this.grade && isFinitePayrollNumber(settings.baseMonthlySalary)
      ? previewGradeMonthlySalary(settings.baseMonthlySalary, this.grade.coefficient) : null;
  }

  get maximumAmount(): number {
    return this.grade ? MAXIMUM_HOURLY_RATE_VALUE * this.grade.coefficient : null;
  }

  get fullHourRate(): number {
    const settings = this.view().settings;
    const norm = settings && settings.workingHoursPerMonth;
    const base = settings && settings.baseMonthlySalary;
    return this.grade && isFinitePayrollNumber(norm) && norm > ZERO && validPreviewBase(base)
      ? previewGradeMonthlySalary(base, this.grade.coefficient) / norm : null;
  }

  get plannedHourRate(): number {
    return this.fullHourRate != null && this.plannedWeight != null ? this.fullHourRate * this.plannedWeight : null;
  }

  get actualHourRate(): number {
    return this.fullHourRate != null && this.plannedWeight != null ? this.fullHourRate * (FULL_WEIGHT - this.plannedWeight) : null;
  }

  private get plannedWeight(): number {
    const weight = this.grade && (this.grade.plannedWeight != null ? this.grade.plannedWeight : this.view().settings.plannedWeight);
    return isFinitePayrollNumber(weight) && weight >= ZERO && weight <= FULL_WEIGHT && hasPreviewScale(weight) ? weight : null;
  }

  get disabled(): boolean { return this.view().editingDisabled || !this.grade; }

  get inputValue(): number | string {
    return this.editing ? this.editingAmount : this.amount;
  }

  startEditing(): void {
    if (this.disabled) { return; }
    this.editingAmount = isFinitePayrollNumber(this.amount) ? Number(payrollNumberInput(this.amount, true)) : null;
    this.editing = true;
  }

  finishEditing(): void { this.editing = false; }

  selectGrade(code: DealPayrollGradeCode): void {
    if (!this.view().editingDisabled && this.grades.some(grade => grade.gradeCode === code)) {
      this.finishEditing();
      this.gradeCode = code;
    }
  }

  reconcileSelection(): void {
    if (this.grade) { return; }
    this.finishEditing();
    this.gradeCode = this.grades.length ? this.grades[ZERO].gradeCode : null;
  }

  changeAmount(amount: number): void {
    if (this.disabled) { return; }
    this.editingAmount = amount;
    const scaled = isFinitePayrollNumber(amount)
      ? amount / this.grade.coefficient * FOUR_DECIMAL_SCALE_FACTOR : NaN;
    this.view().settings.baseMonthlySalary = isFinite(scaled)
      ? Math.round(scaled) / FOUR_DECIMAL_SCALE_FACTOR : null;
    this.changed();
  }

  reset(): void { this.finishEditing(); this.gradeCode = DEFAULT_REFERENCE_GRADE; }
}

export class DealPayrollNumberInputs {
  private activeField = EMPTY_INPUT;

  focus(field: string): void { this.activeField = field; }
  blur(): void { this.activeField = EMPTY_INPUT; }

  display(value: number, field: string): number | string {
    return this.activeField === field || /coefficient/i.test(field) ? value : payrollNumberInput(value);
  }
}

function validPreviewBase(base: number): boolean {
  return isFinitePayrollNumber(base) && base > ZERO && base <= MAXIMUM_HOURLY_RATE_VALUE && hasPreviewScale(base);
}

function hasPreviewScale(value: number): boolean {
  const scaled = value * FOUR_DECIMAL_SCALE_FACTOR;
  return Math.abs(scaled - Math.round(scaled)) < DECIMAL_ROUNDING_TOLERANCE;
}

export function payrollNumberInput(value: number, money: boolean = false): string {
  return isFinitePayrollNumber(value) ? payrollNumberDisplay(value, money) : EMPTY_INPUT;
}

function validReferenceGrade(grade: DealPayrollGrade): boolean {
  if (!grade || grade.enabled !== true || !isFinitePayrollNumber(grade.coefficient)) { return false; }
  const scaled = grade.coefficient * FOUR_DECIMAL_SCALE_FACTOR;
  return grade.coefficient > ZERO && grade.coefficient <= MAXIMUM_COEFFICIENT_VALUE
    && Math.abs(scaled - Math.round(scaled)) < DECIMAL_ROUNDING_TOLERANCE;
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/deal-payroll-group-settings/deal-payroll-group-settings.reference.ts